import * as turf from "@turf/turf"
import {circle, greatCircle, lineIntersect} from "@turf/turf";
import concaveman from "concaveman";
import polylabel from "polylabel";

import type {Feature, Geometry, LineString, MultiLineString, Point, Polygon, Position} from "geojson";
import type {xy} from "@esWebAPI/es/model/Models";

/**
 * Utilities for working with geometries
 */
export class GeomUtils {

    public static types: any = {};

    private static SIMPLIFY_OPTS = {tolerance: 0.01, highQuality: true, mutate: true};

    public static SMALL_GEOM_MIN_VERTEX = 250;

    public static CONCAVE_HULL_MIN_VERTEX = 75;

    private static POLYLABEL_PRECISION = 0.005;

    public static generalizeLines(array: number[], start: number, end: number, threshold: number, result: number[]){
        if(!start){
            start = 0;
        }
        if((!end) && (end !== 0)){
            end = array.length;
        }
        if(!result){
            result = [];
        }
        if((end - start) > 4){
            this._generalizeLines(array, start, end, threshold, result);
            result.push(array[end-2], array[end-1]);
        }else{
            while(start < end){
                result.push(array[start++]);
            }
        }
        return result;
    }

    public static distSq(ax: number, ay: number, bx: number ,by: number){
        const dx = bx - ax;
        const dy = by - ay;
        return (dx*dx) + (dy*dy);
    }

    public static getVertices(geom: Geometry): Position[]|Position[][] {
        if (geom.type === "LineString") {
            return geom.coordinates;
        }
        else if (geom.type === "Polygon") {
            return geom.coordinates[0];
        }
        else if (geom.type === "MultiPolygon") {
            return geom.coordinates.map(poly => poly[0]);
        }
        throw new Error("Unsupported geometry: " + geom.type);
    }

    public static extractLines(positions: Position[]): Position[][] {
        if (positions.length === 2) {
            return [positions];
        }
        const lines: Position[][] = [];
        positions.forEach((p: Position,i) => {
            if (positions[i+1]) {
                lines.push([p, positions[i+1]]);
            }
        });
        return lines
    }

    public static convertLineToPolygon(line: LineString): Polygon {

        if (line.type !== "LineString") {
            throw new Error("Geometry is not a line: " + line);
        }

        return {
            type: "Polygon",
            coordinates: [[...line.coordinates, line.coordinates[0]]]
        };
    }

    public static withGreatCircle(geom: Geometry) {
        if (geom.type === "LineString") {
            let line: LineString|MultiLineString = {type: "LineString", coordinates: []};
            GeomUtils.extractLines((geom as LineString).coordinates).forEach((positions: Position[]) => {
                const greatCircleLine = greatCircle(positions[0], positions[1], {npoints: 50, offset: 50}).geometry;
                if (greatCircleLine.type === "MultiLineString" && line.type === "LineString") {
                    // Convert line to multi line
                    line = {
                        type: "MultiLineString",
                        coordinates: [line.coordinates, ...greatCircleLine.coordinates]
                    }
                }
                else if (greatCircleLine.type === "LineString" && line.type === "MultiLineString") {
                    line.coordinates = [...line.coordinates, greatCircleLine.coordinates]
                }
                else {
                    line.coordinates = [...line.coordinates, ...greatCircleLine.coordinates] as Position[] | Position[][];
                }
            });
            return line;
        }

        else if (geom.type === "Polygon") {
            if (lineIntersect({type: "LineString", coordinates: [[-180,-85], [-180,85]]}, geom).features.length > 0) {
                return geom;
            }
            let coords: Position[] = [];
            GeomUtils.extractLines(geom.coordinates[0]).forEach((positions: Position[]) => {
                const greatCircleLine = (greatCircle(positions[0], positions[1], {npoints: 50}).geometry as LineString)
                coords = [...coords, ...greatCircleLine.coordinates];
            })
            return {
                type: "Polygon",
                coordinates: [coords]
            } as Polygon;
        }
        else {
            throw Error("Unsupported geometry type: " + geom.type);
        }
    }

    private static _generalizeLines(array: number[], start: number, end: number, threshold: number, result: number[]) {
        let s = start;
        const sx = array[s++];
        const sy = array[s++];
        let e = end;
        const ey = array[--e];
        const ex = array[--e];
        let mid = -1;
        let maxDist = -Number.MAX_VALUE;
        while (s < e) {
            const px = array[s++];
            const py = array[s++];
            const dist = Math.abs(es.util.math.pointLineDistSq(px, py, sx, sy, ex, ey));
            if (dist > maxDist) {
                maxDist = dist;
                mid = s;
            }
        }
        if (maxDist <= threshold) {
            result.push(sx, sy); // all points are within threshold - simply add start
        } else {
            this._generalizeLines(array, start, mid, threshold, result);
            this._generalizeLines(array, mid - 2, end, threshold, result);
        }
    }

    /**
     * Convert eS geom format to GeoJson.
     * @param serverGeom e.g. ['PT', -86.82708, 33.4879]
     */
    public static serverToGeoJson(serverGeom: any): Geometry {
        if (!Array.isArray(serverGeom)) {
            throw Error("Invalid geom passed to serverToGeoJson " + serverGeom);
        } else if (serverGeom[0] === "PT") {
            return {
                type: "Point",
                coordinates: serverGeom.slice(1)
            };
        } else if (serverGeom[0] === "PN") {
            const coords = _.chunk<number>(serverGeom.slice(1)[0], 2);
            return {
                type: "Polygon",
                coordinates: [coords]
            };
        } else if (serverGeom[0] === "MP") {
            const multiPolygonCoords: number[][][][] = [];
            const polygons = serverGeom.slice(1);

            for (const polygon of polygons) {
                const coords = _.chunk<number>(polygon[0], 2);
                multiPolygonCoords.push([coords]);
            }

            return {
                type: "MultiPolygon",
                coordinates: multiPolygonCoords
            };
        } else if (serverGeom[0] === "MT") {
            const coords = _.chunk<number>(serverGeom.slice(1), 2);
            return {
                type: "MultiPoint",
                coordinates: coords
            };
        } else {
            throw Error("Unsupported geometry " + serverGeom);
        }
    }

    public static geoJsonToServer(geometry: Geometry) {
        if(geometry.type === "Point") {
            return ["PT", ...geometry.coordinates];
        }

        if(geometry.type === "Polygon") {
            const coords = geometry.coordinates.flatMap(coords => coords.flat());
            return ["PN", coords];
        }

        if(geometry.type === "MultiPolygon") {
            const server:any = ["MP"];
            geometry.coordinates.forEach(coords => {
                const c = coords[0].flat();
                server.push([c]);
            });
            return server;
        }

        throw Error("Invalid geom passed to geoJsonToServer " + geometry);
    }

    /**
     * Converts object from supercluster/children to same structure as geojson feats for consistency when using pbf or geojson.
     * e.g. from:
     * {"id":11403,"attrs":{"attrSet":[{"attrType":"INTEGER","internal":false,"maxSize":38,"name":"NUMBER_FLD","title":"Number"},{"attrType":"STRING","internal":false,"maxSize":40,"name":"SALESREP","title":"Sales Rep"}],"values":{"SALESREP":"Anna","NUMBER_FLD":40396}},"geometry":["PT",-84.404098,30.110181]}{"type":"Feature","properties":{"NUMBER_FLD":3,"SALESREP":"Saliga Machine Co Inc","ES_ID":516,"_azureMapsShapeId":516},"id":516,"geometry":{"type":"Point","coordinates":[-97.4457,27.74658]}}
     * to:
     * {"type":"Feature","properties":{"NUMBER_FLD":3,"SALESREP":"Saliga Machine Co Inc","ES_ID":516,"_azureMapsShapeId":516},"id":516,"geometry":{"type":"Point","coordinates":[-97.4457,27.74658]}}
     */
    public static convertPbfFeatToGeoJsonFeat(input) {
        const { id, attrs, geometry } = input;
        const attrValues = attrs.values;
        const feature = {
            type: "Feature",
            properties: {
                ...attrValues,
                ES_ID: id,
                //_azureMapsShapeId: id
            },
            id: id,
            geometry: {
                type: geometry[0] === "PT" ? "Point" : "Unknown",
                coordinates: [geometry[1], geometry[2]]
            }
        };
        return feature;
    }

    /**
     * Calculate area of MBR, taking curvature of the earth into consideration.
     * e.g.:
     *     const mbr = [-123.2237527, 49.2008636, -123.0235648, 49.3128012];
     *     const mbrArea = calculateMbrArea(mbr);
     *     console.log(mbrArea); // Output: 180.83272148742648
     * @param mbr
     */
    public static calculateMbrArea(mbr: number[]) {
        const R = 6371; // Radius of the Earth in kilometers

        const [minLon, minLat, maxLon, maxLat] = mbr;

        // Convert degrees to radians
        const toRadians = (degree: number) => degree * (Math.PI / 180);
        const minLatRad = toRadians(minLat);
        const minLonRad = toRadians(minLon);
        const maxLatRad = toRadians(maxLat);
        const maxLonRad = toRadians(maxLon);

        // Calculate the length of the sides using the Haversine formula
        const dLat = maxLatRad - minLatRad;
        const dLon = maxLonRad - minLonRad;

        // Calculate lengths of the sides in kilometers
        const sideA = R * dLat;
        const sideB = R * dLon * Math.cos((minLatRad + maxLatRad) / 2);

        // Area of the rectangle in square kilometers
        const area = Math.abs(sideA * sideB);
        return area;
    }

    /**
     * Make sure a Longitude value is within the -180;180 range
     * @param long Longitude value to wrap
     */
    public static wrapLong(long: number) {
        while(long < -180){
            long +=360;
        }
        while (long > 180){
            long -= 360;
        }
        return long;
    }

    private static polygonArea(vertices: Array<Array<number>>) {
        let area = 0;
        for (let i = 0; i < vertices.length; i++) {
            const j = (i + 1) % vertices.length;
            area += vertices[i][0] * vertices[j][1];
            area -= vertices[j][0] * vertices[i][1];
        }
        return area / 2;
    }

    public static isClockwise(vertices: Array<Array<number>>) {
        return GeomUtils.polygonArea(vertices) < 0;
    }

    /**
     * Get the tolerance for simplification based on zoom level.
     * @param zoom current map zoom level e.g. const zoom = this.map._getMap().getZoom();
     */
    public static getToleranceBasedOnZoom(zoom: number) : number {
        const baseTolerance = 0.00001;
        const zoomFactor = 1.5;
        const adjustedTolerance = baseTolerance / Math.pow(zoomFactor, zoom);

        const minTolerance = 0.00001;
        const maxTolerance = 0.3;
        const finalTolerance = Math.max(minTolerance, Math.min(adjustedTolerance, maxTolerance));
        //es.log.log("getToleranceBasedOnZoom", finalTolerance);
        return finalTolerance;
    }

    /**
     * Simplify a polygon.
     * Note: simplify doesn't seem to handle small geoms very well so will avoid simplification if hull is already small (> 250 coords).
     * @param feat modifies feat by ref for performance so es.util.object.deepCopy() the feat first if req'd.
     * @param tolerance default 0.01, see also: #getToleranceBasedOnZoom.
     */
    public static simplifyPolygon(feat: Feature, tolerance = 0.01) {

        if (feat.geometry.coordinates[0].length > GeomUtils.SMALL_GEOM_MIN_VERTEX) {

            const before = feat.geometry.coordinates[0].length;

            // if(es.debugShowOverlayOnSetBounds){
            //     esApp.map.overlay.pulseGeom(feat.geometry, "azure", 5000);
            // }

            const opts = es.util.object.deepCopy(GeomUtils.SIMPLIFY_OPTS);
            opts.tolerance = tolerance;

            turf.simplify(feat.geometry, opts);

            const after = feat.geometry.coordinates[0].length;
            const percentageReduction = ((before - after) / before) * 100;
            es.log.log("simplifyPolygon",
                "simplifyOpts", opts,
                "before", before,
                "after", after,
                "percentageReduction", percentageReduction.toFixed(2) + "%"
            );

            // if(es.debugShowOverlayOnSetBounds){
            //     setTimeout(() => {
            //         // render the simplified version for visual comparison
            //         esApp.map.overlay.pulseGeom(feat.geometry);
            //     }, 2500);
            // }
        }
        return feat;
    }

    /**
     Get concave hull using concaveman
     * @param feats
     */
    public static getConcaveManHull(feats: Feature[]): Feature {
        let allPoints: Position[] = [];
        for(const feat of feats){
            if (feat.geometry.type === "Polygon") {
                allPoints = feat.geometry.coordinates.flat();
            } else if (feat.geometry.type === "MultiPolygon") {
                allPoints = feat.geometry.coordinates.flat(2);
            } else {
                throw new Error("Unsupported geometry type. Only Polygon and MultiPolygon are supported.");
            }
        }

        // hull doesn't seem to handle small geoms very well so will avoid point count is small
        if(allPoints.length < GeomUtils.CONCAVE_HULL_MIN_VERTEX) {
            es.log.log("getConcaveManHull - skip. point count too low", allPoints.length);
            return turf.polygon([allPoints])
        }

        const hullPoints = concaveman(allPoints, 1);

        const concaveHull = turf.polygon([hullPoints]);

        es.log.log("getConcaveManHull", concaveHull);
        //esApp.map.overlay.pulseGeom(concaveHull.geometry, es.util.color.palettes["Blues"][6], 10000);

        return concaveHull;
    }

    /**
     Get concave hull using turf
     * @param feats
     */
    public static getTurfConcaveHull(feats: Feature[]): Feature {
        const allPoints: Feature[] = [];
        for (const feat of feats) {
            if (feat.geometry.type === "Polygon") {
                const coordinates = feat.geometry.coordinates.flat();
                allPoints.push(...coordinates.map(coord => turf.point(coord)));
            } else if (feat.geometry.type === "MultiPolygon") {
                const coordinates = feat.geometry.coordinates.flat(2);
                allPoints.push(...coordinates.map(coord => turf.point(coord)));
            } else {
                throw new Error("Unsupported geometry type. Only Polygon and MultiPolygon are supported.");
            }
        }
        const pointCollection = turf.featureCollection(allPoints);
        const concaveHull = turf.concave(pointCollection, { maxEdge: 0 });
        if (!concaveHull) {
            es.log.error("error getTurfConcaveHull", pointCollection);
            return feats[0];
        }

        es.log.log("getTurfConcaveHull", concaveHull);
        //esApp.map.overlay.pulseGeom(concaveHull.geometry, es.util.color.palettes["Blues"][1], 5000);

        return concaveHull;
    }

    public static polygonRemoveDupVertices(feat: Feature) {

        const fixed = es.util.object.deepCopy(feat);

        const coordinatesEqual = (coord1, coord2) => {
            return coord1[0] === coord2[0] && coord1[1] === coord2[1];
        }

        const coordinates = fixed.geometry.coordinates[0];
        const uniqueCoordinates = [];

        for (let i = 0; i < coordinates.length; i++) {
            if (i === 0 || !coordinatesEqual(coordinates[i], coordinates[i - 1])) {
                uniqueCoordinates.push(coordinates[i]);
            }
        }

        if (!coordinatesEqual(uniqueCoordinates[0], uniqueCoordinates[uniqueCoordinates.length - 1])) {
            uniqueCoordinates.push(uniqueCoordinates[0]);
        }

        const dupCount = fixed.geometry.coordinates[0].length - uniqueCoordinates.length;

        es.log.log("polygonRemoveDupVertices", dupCount ? "Removed " + dupCount + " dup vertices from " + fixed.geometry.coordinates[0].length: "no dups");

        fixed.geometry.coordinates = [uniqueCoordinates];

        return fixed;
    }

    public static unkinkPolygon(feat: Feature): Feature {
        if(!["Polygon", "MultiPolygon"].includes(feat.geometry.type)){
            throw new Error("Unsupported geometry type. Only Polygon and MultiPolygon are supported.");
        }
        let fixed = GeomUtils.polygonRemoveDupVertices(feat);
        try {
            // need to do this first because turf.unkinkPolygon will throw an err if there are dups
            fixed = turf.unkinkPolygon(fixed);
            es.log.log("Repaired Polygon:", fixed, "created", fixed.features.length, "geoms");
            return fixed;
        } catch (err) {
            es.log.warn("err turf.unkinkPolygon", err, feat);
        }
        return feat;
    }

    /**
     * Remove holes from a Polygon or MultiPolygon
     * @param feat
     */
    public static removeHoles(feat: Feature): void {
        if (feat.geometry.type === "Polygon") {
            feat.geometry.coordinates = feat.geometry.coordinates.slice(0, 1);
        } else if (feat.geometry.type === "MultiPolygon") {
            for (let i = 0; i < feat.geometry.coordinates.length; i++) {
                feat.geometry.coordinates[i] = feat.geometry.coordinates[i].slice(0, 1);
            }
        }
    }

    /**
     * Shortens a line by moving the start point toward the end point by a specified amount
     * @param start Start point of the line
     * @param end End point of the line
     * @param offsetDistance Distance to shorten by
     * @returns New start point coordinates
     */
    public static shortenLine(
        start: {x: number, y: number},
        end: {x: number, y: number},
        offsetDistance: number
    ): {x: number, y: number} {

        // Calculate direction vector
        const dx = end.x - start.x;
        const dy = end.y - start.y;

        // Normalize the vector
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length === 0) return start; // Return original point if points are identical

        const normalizedDx = dx / length;
        const normalizedDy = dy / length;

        // Calculate new point offset from start
        return {
            x: start.x + (normalizedDx * offsetDistance),
            y: start.y + (normalizedDy * offsetDistance)
        };
    }

    /**
     * Normalise get vc/centre-point for a feat of any geom type.
     * Will use the eS generated vc prop, if available.
     * @param feat todo: fix geojson Feature or atlas.data.Feature
     * @private
     */
    public static getFeatCentrePoint(feat: Feature): [number, number] {
        let cp;
        if(feat.properties?.vc){
            cp = JSON.parse(feat.properties.vc);
        } else if (feat.geometry?.type === "Point" && feat.geometry?.coordinates){
            cp = feat.geometry.coordinates;
        } else {
            cp = GeomUtils.getPolygonVisualCenter(feat);
        }
        return cp;
    }

    /**
     * Get polygon vc/center-point. Uses the largest (by area) polygon if feat is a MultiPolygon.
     * Note: territoryOutlinesLayerWorker#calculateVisualCenter has a copy of this method so should keep them in sync if possible.
     * @param feat
     */
    public static getPolygonVisualCenter(feat: Feature): number[] | undefined {
        if(feat.geometry.type === "Polygon"){
            //console.log("polylabel getPolygonVisualCenter Polygon", GeomUtils.POLYLABEL_PRECISION, feat);
            return polylabel(feat.geometry.coordinates, GeomUtils.POLYLABEL_PRECISION);
        } else if(feat.geometry.type === "MultiPolygon"){
            try {
                let largestPolygon = turf.polygon(feat.geometry.coordinates[0]);
                let largestPolygonArea = 0;
                for (const coordinates of feat.geometry.coordinates) {
                    const poly = turf.polygon(coordinates);
                    const polyArea = turf.area(poly);
                    if (largestPolygonArea < polyArea) {
                        largestPolygonArea = polyArea;
                        largestPolygon = poly;
                    }
                }
                //console.log("polylabel getPolygonVisualCenter MultiPolygon", GeomUtils.POLYLABEL_PRECISION, feat);
                return polylabel(largestPolygon.geometry.coordinates, GeomUtils.POLYLABEL_PRECISION);
            } catch (e) {
                es.log.warn("calculateVisualCenter: Error creating visual center for geometry", feat);
            }
        }
        es.log.warn("calculateVisualCenter called for unsupported geom type", feat);
        return undefined;
    }

    /**
     * Calculate length between two points. Assumes flat plane (so no curvature of earth accounted for) so best used for pixels etc.
     */
    public static getLineLength(point1: xy, point2: xy): number {
        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Create a Circle geometry from the given point as center
     * @param center
     * @param distanceInMeters
     */
    public static createRadius(center: Point, distanceInMeters: number) {
        return circle(center, distanceInMeters, {
            units: "meters"
        });
    }
}
